import React, { useState, useMemo } from 'react';
import { styled } from 'styletron-react';
import { HeadingMedium, ParagraphMedium } from 'baseui/typography';
import { TableContent } from '../LayoutPage';
import { MantineDataTable } from '../../components/MantineDataTable';
import { MRT_ColumnDef } from 'mantine-react-table';
import { useInvitationTypes } from '../../hooks/useInvitationTypes';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import { InvitationTypeDetailDrawer } from '../../components/InvitationTypeDetailDrawer';
import type { InvitationType } from '../../api/generated';
import { Plus, Tag } from 'lucide-react';

const Container = styled('div', {
  padding: '24px',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
});

const Header = styled('div', {
  marginBottom: '24px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
});

const HeaderLeft = styled('div', {});

const TableContainer = styled('div', {
  flex: 1,
  backgroundColor: '#FFFFFF',
  borderRadius: '8px',
  padding: '20px',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
});

const EmptyState = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '48px',
  gap: '16px',
  color: '#6B7280',
});

const AddButton = styled('button', {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '10px 20px',
  borderRadius: '8px',
  border: 'none',
  backgroundColor: 'var(--tenant-primary, #111827)',
  color: '#fff',
  fontSize: '14px',
  fontWeight: 500,
  cursor: 'pointer',
  transition: 'all 0.15s ease',
  ':hover': { backgroundColor: 'color-mix(in srgb, var(--tenant-primary, #111827) 85%, black)' },
});

const NumBadge = styled('span', {
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: '8px',
  fontSize: '12px',
  fontWeight: 600,
  backgroundColor: '#F3F4F6',
  color: '#374151',
});

// SAM-946: gate rules are seen by the tenant admin and Xhinola only (isAdmin).
const gateColumns: MRT_ColumnDef<InvitationType>[] = [
  {
    accessorKey: 'external_gate_rules',
    header: 'External Gate Rules',
    size: 180,
    Cell: ({ cell }) => {
      const v = cell.getValue<string | null>();
      return v ? <span>{v}</span> : <span style={{ color: '#9CA3AF' }}>—</span>;
    },
  },
  {
    accessorKey: 'internal_gate_rules',
    header: 'Internal Gate Rules',
    size: 180,
    Cell: ({ cell }) => {
      const v = cell.getValue<string | null>();
      return v ? <span>{v}</span> : <span style={{ color: '#9CA3AF' }}>—</span>;
    },
  },
];

export const InvitationTypes = () => {
  const { invitationTypes, isLoading } = useInvitationTypes();
  // SAM-946: gate rules are seen and edited by the tenant admin and Xhinola only (isAdmin).
  const { isAdmin: seesGateRules } = useUserPermissions();
  const [selected, setSelected] = useState<InvitationType | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'create' | 'edit'>('edit');

  const columns = useMemo<MRT_ColumnDef<InvitationType>[]>(
    () => [
      {
        accessorKey: 'id',
        header: 'ID',
        size: 60,
      },
      {
        accessorKey: 'name',
        header: 'Name',
        size: 200,
      },
      {
        accessorKey: 'duration_limit_days',
        header: 'Duration (days)',
        size: 140,
        Cell: ({ cell }) => {
          const v = cell.getValue<number | null>();
          return v != null ? <NumBadge>{v}</NumBadge> : <span style={{ color: '#9CA3AF' }}>—</span>;
        },
      },
      {
        accessorKey: 'max_adults',
        header: 'Max Adults',
        size: 110,
        Cell: ({ cell }) => {
          const v = cell.getValue<number | null>();
          return v != null ? <NumBadge>{v}</NumBadge> : <span style={{ color: '#9CA3AF' }}>—</span>;
        },
      },
      {
        accessorKey: 'max_children',
        header: 'Max Children',
        size: 120,
        Cell: ({ cell }) => {
          const v = cell.getValue<number | null>();
          return v != null ? <NumBadge>{v}</NumBadge> : <span style={{ color: '#9CA3AF' }}>—</span>;
        },
      },
      {
        accessorKey: 'max_guests_per_day',
        header: 'Max/Day',
        size: 100,
        Cell: ({ cell }) => {
          const v = cell.getValue<number | null>();
          return v != null ? <NumBadge>{v}</NumBadge> : <span style={{ color: '#9CA3AF' }}>—</span>;
        },
      },
      ...(seesGateRules ? gateColumns : []),
    ],
    [seesGateRules]
  );

  const handleRowClick = (row: InvitationType) => {
    setSelected(row);
    setDrawerMode('edit');
    setDrawerOpen(true);
  };

  const handleCreate = () => {
    setSelected(null);
    setDrawerMode('create');
    setDrawerOpen(true);
  };

  const handleClose = () => {
    setDrawerOpen(false);
    setSelected(null);
  };

  return (
    <TableContent>
      <Container>
        <Header>
          <HeaderLeft>
            <HeadingMedium marginTop="0" marginBottom="8px">
              Pass types
            </HeadingMedium>
            <ParagraphMedium margin={0} color="#6B7280">
              Manage the pass types available in the application
            </ParagraphMedium>
          </HeaderLeft>
          <AddButton onClick={handleCreate}>
            <Plus size={16} />
            Add Type
          </AddButton>
        </Header>

        <TableContainer>
          {invitationTypes.length === 0 && !isLoading ? (
            <EmptyState>
              <Tag size={48} color="#D1D5DB" />
              <div style={{ textAlign: 'center' }}>
                <ParagraphMedium margin={0} style={{ fontWeight: 500 }}>
                  No pass types found
                </ParagraphMedium>
                <ParagraphMedium margin="8px 0 0 0" style={{ fontSize: '14px' }}>
                  Click "Add Type" to create a new pass type
                </ParagraphMedium>
              </div>
            </EmptyState>
          ) : (
            <MantineDataTable
              columns={columns}
              data={invitationTypes}
              loading={isLoading}
              enablePagination
              enableSorting
              enableGlobalFilter={false}
              initialPageSize={20}
              pageSizeOptions={[10, 20, 50]}
              onRowClick={handleRowClick}
            />
          )}
        </TableContainer>

        <InvitationTypeDetailDrawer
          isOpen={drawerOpen}
          onClose={handleClose}
          invitationType={selected}
          mode={drawerMode}
        />
      </Container>
    </TableContent>
  );
};
